/**
 * <AuthBrandCanvas> — the photo panel at the inline-end of the auth shell
 * (shown on `md+` only).
 *
 * An office photograph under a deep navy overlay, and on it:
 *   - the LinkHR wordmark (white) at the top-start;
 *   - a headline + one-line pitch + three stats (support hours, satisfaction,
 *     employees on the platform) — all i18n copy, so the numbers are editable
 *     without a build;
 *   - the Link Business Solutions lockup at the bottom-start and a small
 *     Hijri · Gregorian imprint at the bottom-end.
 *
 * The panel forces the DARK token set on its own subtree (`data-theme="dark"`)
 * so every text/muted/primary token resolves to values that read on the
 * photo, in either app theme. Tenant color never reaches this panel any more
 * (it lives on the form side); tenant logos render in the form header.
 * Decorative elements are `aria-hidden`; the copy stays in the a11y tree.
 */
import { useMemo, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { intlDateLocale, metaSeparator } from "@linkhr/i18n";
import canvasPhoto from "./assets/auth-canvas.jpg";
import { LbsLogo } from "./brand/LbsLogo.js";
import { LinkHrWordmark } from "./brand/LinkHrWordmark.js";
import type { AuthBranding } from "./useAuthBranding.js";

export interface AuthBrandCanvasProps {
  /** Kept for API compatibility; the canvas is tenant-neutral now. */
  branding?: AuthBranding | undefined;
  /** Whose sign-in this is. Default Link HR. */
  product?: "hr" | "accounting";
  className?: string;
}

/**
 * Today's date in Hijri (umm-al-qura) + Gregorian — a static render snapshot.
 *
 * Returns the two calendar segments SEPARATELY so the caller can bidi-isolate
 * each in a `<bdi>`: the Hijri segment is Arabic (strong RTL) and would
 * otherwise split the Latin-digit Gregorian date around itself in LTR.
 */
function dualCalendarSnapshot(lang: string): { hijri: string; greg: string } {
  const now = new Date();
  // Resolve both calendars in the Jordan business timezone so the snapshot
  // shows the Amman calendar day, not the viewer's browser day near midnight.
  let hijri = "";
  try {
    hijri = new Intl.DateTimeFormat("ar-SA-u-ca-islamic-umalqura", {
      timeZone: "Asia/Amman",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(now);
  } catch {
    hijri = "";
  }
  const greg = new Intl.DateTimeFormat(intlDateLocale(lang), {
    timeZone: "Asia/Amman",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(now);
  return { hijri, greg };
}

const STATS = ["support", "satisfaction", "employees"] as const;

export function AuthBrandCanvas({
  product = "hr",
  className = "",
}: AuthBrandCanvasProps): JSX.Element {
  const { t, i18n } = useTranslation("auth");
  const dualCalendar = useMemo(() => dualCalendarSnapshot(i18n.language), [i18n.language]);

  return (
    <div
      data-theme="dark"
      className={`relative h-full w-full overflow-hidden bg-cover bg-center ${className}`}
      style={{ backgroundImage: `url(${canvasPhoto})` }}
    >
      {/* Navy overlay — keeps every token on the photo legible. */}
      <div aria-hidden="true" className="bg-rail/75 pointer-events-none absolute inset-0" />

      <div className="relative flex h-full flex-col justify-between gap-xl p-2xl">
        <div className="text-default">
          {product === "accounting" ? (
            <p className="text-xl font-bold">{t("brand.accounting_name")}</p>
          ) : (
            <LinkHrWordmark height={28} tone="dark" />
          )}
        </div>

        <div className="flex flex-col gap-lg">
          <div className="flex flex-col gap-sm">
            <h2 className="text-default text-[2.5rem] leading-tight font-bold">
              {product === "accounting" ? t("brand.accounting_hero_title") : t("brand.hero_title")}
            </h2>
            <p className="text-muted max-w-[60ch] text-sm">
              {product === "accounting" ? t("brand.accounting_hero_sub") : t("brand.hero_sub")}
            </p>
          </div>
          {/* The stats are Link HR's own; Link Accounting has none to claim yet. */}
          <dl className={product === "accounting" ? "hidden" : "flex flex-wrap gap-2xl"}>
            {STATS.map((key) => (
              // DOM order is dt → dd (a valid <dl>); the value reads above its
              // label through column-reverse.
              <div key={key} className="flex flex-col-reverse gap-xs">
                <dt className="text-muted text-sm">{t(`brand.stat_${key}_label`)}</dt>
                {/* Product teal, not the tenant primary: this is Link HR's own
                    pitch, so it stays brand-coloured on every tenant's login. */}
                <dd className="text-viz-1 text-2xl font-bold">
                  <bdi>{t(`brand.stat_${key}_value`)}</bdi>
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="flex items-end justify-between gap-md">
          <div className="text-default">
            <LbsLogo height={40} tone="dark" />
          </div>
          {/* Each calendar segment is bidi-isolated — see dualCalendarSnapshot. */}
          <p aria-hidden="true" className="text-muted font-mono text-caption">
            {dualCalendar.hijri.length > 0 ? (
              <>
                <bdi>{dualCalendar.hijri}</bdi>
                {metaSeparator()}
                <bdi>{dualCalendar.greg}</bdi>
              </>
            ) : (
              <bdi>{dualCalendar.greg}</bdi>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
